import { queryOptions, skipToken, useQuery } from '@tanstack/react-query';

import { PriceStatus } from 'chaika-enums';
import { PriceData, PurchasePriceData } from 'chaika-frontend-types/frontend-cms';
import { PriceApi } from '@api/PriceApi';
import { getUniqIds } from '@utils/getUniqIds';
import { getUniqSortedIds } from '@utils/getUniqSortedIds';
import { getUniqChunkedIds } from '@utils/getUniqChunkedIds';
import { STALE_TIME_FIVE_MINUTES_MS } from '@utils/staleTime';

export const priceKeys = {
    all: ['price'] as const,
    contractorPrices: (serviceId: string, legalEntityId?: string) =>
        [...priceKeys.all, 'contractorPrices', serviceId, legalEntityId ?? null] as const,
    purchasePrices: () => [...priceKeys.all, 'purchasePrices'] as const,
    purchasePricesByServiceIds: (serviceIds: string[]) =>
        [...priceKeys.purchasePrices(), getUniqSortedIds(serviceIds)] as const,
    compositeServiceIds: (serviceId: string) => [...priceKeys.all, 'compositeServiceIds', serviceId] as const,
    byServiceIds: (serviceIds: string[], legalEntityIds: string[] | undefined, status: PriceStatus | undefined) =>
        [
            ...priceKeys.all,
            'byServiceIds',
            getUniqSortedIds(serviceIds),
            legalEntityIds ?? null,
            status ?? null,
        ] as const,
};

export const priceQueryOptions = {
    byServiceIds: (serviceIds: string[], legalEntityIds?: string[], status?: PriceStatus) =>
        queryOptions({
            staleTime: STALE_TIME_FIVE_MINUTES_MS,
            queryKey: priceKeys.byServiceIds(serviceIds, legalEntityIds, status),
            queryFn:
                serviceIds.length > 0 ? () => fetchPricesByServiceIds(serviceIds, legalEntityIds, status) : skipToken,
        }),
    contractorPrices: (serviceId: string, legalEntityId?: string) =>
        queryOptions({
            queryKey: priceKeys.contractorPrices(serviceId, legalEntityId),
            queryFn: async (): Promise<PriceData[]> => {
                const { prices } = await PriceApi.getPrices({
                    serviceId,
                    onlyContractor: true,
                    status: PriceStatus.Active,
                    legalEntityIds: legalEntityId,
                });

                return prices;
            },
        }),
    purchasePrices: (serviceIds: string[]) =>
        queryOptions({
            staleTime: STALE_TIME_FIVE_MINUTES_MS,
            queryKey: priceKeys.purchasePricesByServiceIds(serviceIds),
            queryFn: (): Promise<PurchasePriceData[]> =>
                PriceApi.getPurchasePrices({ serviceId: getUniqSortedIds(serviceIds) }),
        }),
    compositeServiceIds: (serviceId: string) =>
        queryOptions({
            staleTime: STALE_TIME_FIVE_MINUTES_MS,
            queryKey: priceKeys.compositeServiceIds(serviceId),
            queryFn: async (): Promise<string[]> => {
                const { prices } = await PriceApi.getCompositeServices({ serviceId, status: PriceStatus.Active });

                return prices[0]?.includedServices ?? [];
            },
        }),
};

export function getContractorIdsFromPrices(prices: PriceData[]): string[] {
    return getUniqIds(prices.map(({ contractorId }) => contractorId).filter((id): id is string => !!id));
}

export function useContractorPrice(params: {
    serviceId?: string;
    contractorId?: string;
    legalEntityId?: string;
}): PriceData | undefined {
    const { serviceId, contractorId, legalEntityId } = params;

    const { data: contractorPrices = [] } = useQuery({
        ...priceQueryOptions.contractorPrices(serviceId ?? '', legalEntityId),
        enabled: !!serviceId && !!contractorId && !!legalEntityId,
    });

    return contractorId ? contractorPrices.find(it => it.contractorId === contractorId) : undefined;
}

async function fetchPricesByServiceIds(
    serviceIds: string[],
    legalEntityIds?: string[],
    status?: PriceStatus,
): Promise<PriceData[]> {
    const responses = await Promise.all(
        getUniqChunkedIds(serviceIds).map(ids =>
            PriceApi.getPrices({ status, serviceId: ids.join(), legalEntityIds: legalEntityIds?.join() }),
        ),
    );

    return responses.flatMap(({ prices }) => prices);
}
